import { useState } from "react";
import { UpdateShippingDetails } from "../../Models/Order";
import { ShippingDetails } from "../../Models/ShippingDetails";
import { ShippingMethod } from "../../Enums/ShippingMethod";
import { getShippingMethodLabel } from "../../Utils/Enums";
import { formatCnpj, formatPriceBRL, unformat } from '../../Utils/DataFormatters';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import FloatingInput from '../FloatingInput/FloatingInput';
import Modal from '../Modal/Modal';
import Button from "../Button/Button";

type ShippingDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderNumber: number;
  shippingDetails: ShippingDetails;
  onSave: (data: UpdateShippingDetails) => void;
  isSaving?: boolean;
};

const ShippingDetailsModal = ({ 
  isOpen,
  onClose,
  orderNumber,
  shippingDetails,
  onSave,
  isSaving = false
}: ShippingDetailsModalProps) => {
  const [formData, setFormData] = useState<UpdateShippingDetails>({
    orderId: shippingDetails.orderId,
    shippingDetailsId: shippingDetails.id,
    carrierName: shippingDetails.carrierName ?? "",
    carrierCnpj: shippingDetails.carrierCnpj ?? "",
    price: shippingDetails.price ?? null,
    estimatedTimeInDays: shippingDetails.estimatedTimeInDays ?? null,
    estimatedDeliveryDate: shippingDetails.estimatedDeliveryDate ?? null,
    isSenderResponsible: shippingDetails.isSenderResponsible,
    isCompanyDelivery: shippingDetails.isCompanyDelivery ?? false
  });

  const isCustomCarrier = shippingDetails.shippingMethod === ShippingMethod.CustomCarrier;
  const normalizedEstimatedDeliveryDate = formData.estimatedDeliveryDate
    ? formData.estimatedDeliveryDate.split("T")[0]
    : "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;

    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSenderResponsibleChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      isSenderResponsible: checked,
      price: checked ? 0 : prev.price
    }));
  };

  const handleCompanyDeliveryChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      isCompanyDelivery: checked,
      carrierName: checked ? "" : prev.carrierName,
      carrierCnpj: checked ? "" : prev.carrierCnpj,
      isSenderResponsible: checked ? false : prev.isSenderResponsible
    }));
  };

  const handleSubmitShippingDetails = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const carrierName = (formData.carrierName ?? "").trim();
    const carrierCnpj = unformat(formData.carrierCnpj ?? "").trim();
    const priceValue = formData.price ?? null;
    const estimatedDays = formData.estimatedTimeInDays ?? null;
    const estimatedDate = formData.estimatedDeliveryDate;

    const estimatedDaysIsValid =
      estimatedDays === null ||
      (!isNaN(Number(estimatedDays)) && Number(estimatedDays) > 0);
    const priceIsValid = formData.isSenderResponsible
      ? true
      : priceValue !== null && !isNaN(Number(priceValue)) && Number(priceValue) > 0;

    if (formData.isCompanyDelivery) {
      if (!priceIsValid || !estimatedDaysIsValid) {
        toast.error("Preencha todos os campos obrigatórios antes de salvar.");
        return;
      }
    } else if (!carrierName || !carrierCnpj || !estimatedDaysIsValid || !priceIsValid) {
      toast.error("Preencha todos os campos obrigatórios antes de salvar.");
      return;
    }

    const dataToSave = {
      ...formData,
      carrierName: formData.isCompanyDelivery ? "" : carrierName,
      carrierCnpj: formData.isCompanyDelivery ? "" : carrierCnpj,
      price: formData.isSenderResponsible ? 0 : Number(formData.price),
      estimatedTimeInDays: isCustomCarrier ? null : (formData.estimatedTimeInDays == null ? null : Number(formData.estimatedTimeInDays)),
      estimatedDeliveryDate: isCustomCarrier ? (estimatedDate ?? null) : null
    };

    onSave(dataToSave);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal overlayClassName="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-white rounded-md shadow-xl px-5 md:px-6 py-8 md:py-9 relative w-full max-w-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-primary-600 hover:text-primary-700 text-2xl font-bold"
        >
          <FaTimes />
        </button>
        <h3 className="text-lg font-bold text-primary-600 mb-4 md:mb-2">{"Pedido"} #{orderNumber}</h3>
        <h4 className="text-md font-semibold mb-2 md:mb-2">{"Editar informações de envio"}</h4>
        <form className="flex flex-col gap-3 md:gap-4" onSubmit={handleSubmitShippingDetails}>
          <FloatingInput
            label={"Método de entrega"}
            name="shippingMethod"
            value={getShippingMethodLabel(shippingDetails.shippingMethod)}
            readOnly
          />

          {formData.isCompanyDelivery ? (
            <>
              <FloatingInput
                label={"Valor"}
                name="price"
                type="text"
                value={formData.price === null ? "" : formatPriceBRL(formData.price!)}
                onChange={(e) => {
                  const raw = e.target.value;
                  const numericString = raw.replace(/\D/g, '');
                  const numberValue = numericString ? parseFloat(numericString) / 100 : null;

                  setFormData((prev) => ({
                    ...prev,
                    price: numberValue,
                  }));
                }}
                required
              />

              <FloatingInput
                label={"Entrega estimada"}
                name="estimatedDeliveryDate"
                type="date"
                value={normalizedEstimatedDeliveryDate}
                onChange={handleChange}
              />
            </>
          ) : (
            <>
              <FloatingInput
                label={"Nome da transportadora"}
                name="carrierName"
                value={formData.carrierName ?? ""}
                onChange={handleChange}
                required
              />

              <FloatingInput
                label={"CNPJ da transportadora"}
                name="carrierCnpj"
                value={formatCnpj(formData.carrierCnpj ?? "")}
                onChange={handleChange}
                required
              />

              <FloatingInput
                label={"Valor"}
                name="price"
                type="text"
                value={formData.price === null ? "" : formatPriceBRL(formData.price!)}
                readOnly={formData?.isSenderResponsible}
                onChange={(e) => {
                  const raw = e.target.value;
                  const numericString = raw.replace(/\D/g, '');
                  const numberValue = numericString ? parseFloat(numericString) / 100 : null;

                  setFormData((prev) => ({
                    ...prev,
                    price: numberValue,
                  }));
                }}
                required={!formData.isSenderResponsible}
              />

              <FloatingInput
                label={isCustomCarrier ? "Entrega estimada" : "Tempo estimado em dias"}
                name={isCustomCarrier ? "estimatedDeliveryDate" : "estimatedTimeInDays"}
                type={isCustomCarrier ? "date" : "number"}
                value={
                  isCustomCarrier
                    ? normalizedEstimatedDeliveryDate
                    : formData.estimatedTimeInDays ?? ""
                }
                onChange={handleChange}
                required={!isCustomCarrier}
              />

              <FloatingInput
                label={"Frete por conta do remetente"}
                type="checkbox"
                name="isSenderResponsible"
                checked={formData?.isSenderResponsible}
                onChange={(e) => handleSenderResponsibleChange(e.target.checked)}
              />
            </>
          )}

          <FloatingInput
            label={"Entrega com transporte próprio"}
            type="checkbox"
            name="isCompanyDelivery"
            checked={formData.isCompanyDelivery}
            onChange={(e) => handleCompanyDeliveryChange(e.target.checked)}
          />

          <div className="w-full flex flex-col md:flex-row items-center justify-center gap-2 md:mt-2">
            <Button variant="secondary"
              type="button"
              onClick={onClose}
              className="w-full px-16 py-2"
              disabled={isSaving}
            >
              {"CANCELAR"}
            </Button>
            <Button isLoading={isSaving}
              type="submit"
              className="w-full px-16 py-2"
              disabled={isSaving}
            >
              {"SALVAR"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ShippingDetailsModal;
